/**
 * CodeInventoryReport Component
 *
 * Professional code inventory report with comprehensive file and program listing.
 * Features searchable/filterable table with export capabilities.
 *
 * Features:
 * - Searchable data table
 * - Column sorting
 * - Export to CSV/Excel
 * - Pagination
 * - Professional styling
 * - Filter by file type, complexity, LOC range
 */

import { useState, useMemo, useRef, useEffect } from 'react'
import * as d3 from 'd3'
import * as echarts from 'echarts'
import {
  FileCode, Search, Download, Filter, ChevronUp, ChevronDown,
  FileText, TrendingUp, AlertTriangle, CheckCircle, X
} from 'lucide-react'
import './CodeInventoryReport.css'

/**
 * Calculate complexity score from structural indicators.
 * comprehensive_parse_results has paragraphs[], calls[], copy_statements[], data_items[]
 * but NOT a classifications{} map — that's only in line_inventory.json per program.
 * We derive complexity from what we have: paragraphs drive control flow, calls are external
 * coupling, copybooks add data complexity.
 */
function calculateComplexity(program) {
  const paraCount = program?.paragraphs?.length || 0
  const callCount = program?.calls?.length || 0
  const copyCount = program?.copy_statements?.length || 0
  // Weight: each paragraph = 2 (control paths), each call = 3 (external coupling), each copybook = 1
  return paraCount * 2 + callCount * 3 + copyCount + 1
}

/**
 * Get complexity level badge
 */
function getComplexityBadge(complexity) {
  if (complexity <= 10) return { level: 'low', label: 'Low', color: '#9ece6a' }
  if (complexity <= 20) return { level: 'medium', label: 'Medium', color: '#7dcfff' }
  if (complexity <= 50) return { level: 'high', label: 'High', color: '#e0af68' }
  return { level: 'critical', label: 'Critical', color: '#f7768e' }
}

/**
 * Export to CSV
 */
function exportToCSV(data, filename = 'code-inventory.csv') {
  const headers = ['File Name', 'LOC', 'Code Lines', 'Comment Lines', 'Complexity', 'Level', 'Copybooks', 'Paragraphs']
  const rows = data.map(item => [
    item.fileName,
    item.loc,
    item.codeLoc,
    item.commentLoc,
    item.complexity,
    item.complexityLevel,
    item.copybooks,
    item.paragraphs,
  ])

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
}

/** Complexity level → fill color */
const COMPLEXITY_COLORS = {
  low:      '#9ece6a',
  medium:   '#7dcfff',
  high:     '#e0af68',
  critical: '#f7768e',
}

/**
 * ECharts scatter — X=LOC, Y=complexity, size=paragraphs, color=complexity level
 */
const COMPLEXITY_SCATTER_COLORS = {
  low:      '#9ece6a',
  medium:   '#7dcfff',
  high:     '#e0af68',
  critical: '#f7768e',
}

function ScatterSection({ data }) {
  const chartRef    = useRef(null)
  const instanceRef = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data?.length) return

    if (instanceRef.current) {
      instanceRef.current.dispose()
    }

    const chart = echarts.init(chartRef.current)
    instanceRef.current = chart

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const d = params.data
          return `<div style="font-size:12px;padding:4px 0"><strong>${d.name}</strong><br/>LOC: ${d.value[0]}<br/>Complexity: ${d.value[1]}<br/>Paragraphs: ${d.paragraphs}<br/>Level: <span style="color:${COMPLEXITY_SCATTER_COLORS[d.level]}">${d.level}</span></div>`
        },
        backgroundColor: '#16161e',
        borderColor: '#3b4261',
        textStyle: { color: '#c0caf5' },
      },
      grid: { left: 50, right: 20, top: 20, bottom: 40 },
      xAxis: {
        name: 'Lines of Code',
        nameLocation: 'middle',
        nameGap: 28,
        nameTextStyle: { color: '#565f89', fontSize: 11 },
        splitLine: { lineStyle: { color: '#2a2b3d' } },
        axisLine: { lineStyle: { color: '#3b4261' } },
        axisLabel: { color: '#565f89', fontSize: 10 },
        axisTick: { lineStyle: { color: '#3b4261' } },
      },
      yAxis: {
        name: 'Complexity',
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: { color: '#565f89', fontSize: 11 },
        splitLine: { lineStyle: { color: '#2a2b3d' } },
        axisLine: { lineStyle: { color: '#3b4261' } },
        axisLabel: { color: '#565f89', fontSize: 10 },
        axisTick: { lineStyle: { color: '#3b4261' } },
      },
      series: [{
        type: 'scatter',
        data: data.map(d => ({
          value: [d.loc || 0, d.complexity || 0],
          name: d.fileName || '',
          paragraphs: d.paragraphs || 0,
          level: d.complexityLevel || 'low',
          itemStyle: {
            color: COMPLEXITY_SCATTER_COLORS[d.complexityLevel] || '#565f89',
            opacity: 0.82,
          },
        })),
        symbolSize: (val, params) => {
          const p = params.data.paragraphs || 1
          return Math.max(10, Math.min(36, 6 + p * 2))
        },
        label: {
          show: true,
          formatter: (params) => params.data.name,
          position: 'top',
          color: '#787c99',
          fontSize: 9,
        },
      }],
    }

    chart.setOption(option)

    const handleResize = () => chart.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.dispose()
    }
  }, [data])

  if (!data?.length) return null

  // Legend
  const levels = [
    { key: 'low',      label: 'Low' },
    { key: 'medium',   label: 'Medium' },
    { key: 'high',     label: 'High' },
    { key: 'critical', label: 'Critical' },
  ].filter(l => data.some(d => d.complexityLevel === l.key))

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {levels.map(l => (
          <span key={l.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#787c99' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: COMPLEXITY_SCATTER_COLORS[l.key], display: 'inline-block' }} />
            {l.label}
          </span>
        ))}
        <span style={{ fontSize: '11px', color: '#565f89', marginLeft: 'auto' }}>size = paragraphs</span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height: '260px', background: '#16161e', borderRadius: '8px' }} />
    </div>
  )
}

/**
 * D3 treemap — size = LOC, color = complexity level.
 * Renders inside a 260px tall responsive container.
 */
function TreemapSection({ data }) {
  const containerRef = useRef(null)
  const svgRef       = useRef(null)

  useEffect(() => {
    if (!data?.length || !containerRef.current || !svgRef.current) return

    const container = containerRef.current
    const width  = container.clientWidth  || 700
    const height = 240

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    // Build hierarchy — single virtual root, children = programs
    const root = d3.hierarchy({ children: data })
      .sum(d => d.loc || 1)
      .sort((a, b) => b.value - a.value)

    d3.treemap()
      .size([width, height])
      .paddingInner(2)
      .paddingOuter(0)
      (root)

    const cell = svg.selectAll('g')
      .data(root.leaves())
      .enter()
      .append('g')
      .attr('transform', d => `translate(${d.x0},${d.y0})`)

    cell.append('rect')
      .attr('width',  d => Math.max(0, d.x1 - d.x0))
      .attr('height', d => Math.max(0, d.y1 - d.y0))
      .attr('fill',   d => COMPLEXITY_COLORS[d.data.complexityLevel] || '#565f89')
      .attr('rx', 3)
      .attr('opacity', 0.85)
      .style('cursor', 'default')

    // Label — only shown when rectangle is large enough
    cell.each(function(d) {
      const rectW = d.x1 - d.x0
      const rectH = d.y1 - d.y0
      if (rectW < 30 || rectH < 18) return

      const g = d3.select(this)

      // Program name (truncated)
      const maxChars = Math.floor(rectW / 7)
      const name = d.data.fileName || ''
      const label = name.length > maxChars ? name.slice(0, maxChars - 1) + '…' : name

      g.append('text')
        .attr('x', 5)
        .attr('y', 14)
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', '#1a1b26')
        .attr('pointer-events', 'none')
        .text(label)

      // LOC sub-label — only if there's room vertically
      if (rectH >= 34) {
        g.append('text')
          .attr('x', 5)
          .attr('y', 27)
          .attr('font-size', '10px')
          .attr('fill', 'rgba(26,27,38,0.7)')
          .attr('pointer-events', 'none')
          .text(`${(d.data.loc || 0).toLocaleString()} LOC`)
      }
    })

    // Tooltip on hover
    cell.append('title')
      .text(d =>
        `${d.data.fileName}\n` +
        `LOC: ${(d.data.loc || 0).toLocaleString()}\n` +
        `Complexity: ${d.data.complexityLabel} (${d.data.complexity})\n` +
        `Paragraphs: ${d.data.paragraphs}  ·  Ext. Calls: ${d.data.calls}`
      )

  }, [data])

  if (!data?.length) return null

  // Legend
  const levels = [
    { key: 'low',      label: 'Low' },
    { key: 'medium',   label: 'Medium' },
    { key: 'high',     label: 'High' },
    { key: 'critical', label: 'Critical' },
  ].filter(l => data.some(d => d.complexityLevel === l.key))

  return (
    <div style={{ margin: '0 0 20px 0' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#a9b1d6' }}>
          Program Complexity Treemap
          <span style={{ fontSize: '11px', fontWeight: 400, color: '#565f89', marginLeft: '8px' }}>
            size = lines of code · color = complexity
          </span>
        </span>
        <div style={{ display: 'flex', gap: '10px' }}>
          {levels.map(l => (
            <span key={l.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#787c99' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: COMPLEXITY_COLORS[l.key], display: 'inline-block' }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>
      {/* SVG container */}
      <div ref={containerRef} style={{ width: '100%', height: '240px', background: '#16161e', borderRadius: '8px', overflow: 'hidden' }}>
        <svg ref={svgRef} style={{ display: 'block' }} />
      </div>
    </div>
  )
}

/**
 * Main CodeInventoryReport component
 */
function CodeInventoryReport({ comprehensiveData, crossReference, procedureModel, title = 'Code Inventory Report' }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('fileName')
  const [sortDirection, setSortDirection] = useState('asc')
  const [filterLevel, setFilterLevel] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [vizTab, setVizTab] = useState('scatter')
  const itemsPerPage = 20

  // Parse inventory data
  const inventoryData = useMemo(() => {
    if (!comprehensiveData?.programs) return []

    return Object.entries(comprehensiveData.programs).map(([fileName, program]) => {
      // comprehensive_parse_results.programs[name] has:
      //   line_count, paragraphs[], copy_statements[], calls[], data_items[], file_operations{}
      // It does NOT have a classifications{} map — that's in line_inventory.json per program.
      const complexity = calculateComplexity(program)
      const { level, label } = getComplexityBadge(complexity)

      const paraCount = program.paragraphs?.length || 0
      const callCount = program.calls?.length || 0
      const copyCount = program.copy_statements?.length || 0
      const dataItemCount = program.data_items?.length || 0

      // File operations is an object {open:…, read:…, write:…} — count keys
      const fileOpsObj = program.file_operations || {}
      const fileOpsCount = Array.isArray(fileOpsObj)
        ? fileOpsObj.length
        : Object.keys(fileOpsObj).length

      return {
        fileName: program.file_name || fileName,
        filePath: program.file_path || '',
        loc: program.line_count || 0,
        codeLoc: 0,          // not available without per-program line_inventory
        commentLoc: 0,
        blankLoc: 0,
        dataLoc: dataItemCount,
        copybooks: copyCount,
        paragraphs: paraCount,
        calls: callCount,
        dataItems: dataItemCount,
        fileOps: fileOpsCount,
        sections: 0,
        divisions: 0,
        complexity,
        complexityLevel: level,
        complexityLabel: label,
        ifCount: 0,
        performCount: paraCount,  // paragraphs drive PERFORM-style control flow
        gotoCount: 0,
      }
    })
  }, [comprehensiveData])

  // Filter and sort
  const filteredAndSortedData = useMemo(() => {
    let result = [...inventoryData]

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(item =>
        item.fileName.toLowerCase().includes(term) ||
        item.filePath.toLowerCase().includes(term)
      )
    }

    // Complexity filter
    if (filterLevel !== 'all') {
      result = result.filter(item => item.complexityLevel === filterLevel)
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    return result
  }, [inventoryData, searchTerm, sortField, sortDirection, filterLevel])

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage)
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Summary stats
  const totalLOC = inventoryData.reduce((sum, item) => sum + item.loc, 0)
  const totalParagraphs = inventoryData.reduce((sum, item) => sum + item.paragraphs, 0)
  const avgComplexity = inventoryData.length > 0
    ? Math.round(inventoryData.reduce((sum, item) => sum + item.complexity, 0) / inventoryData.length)
    : 0

  const complexityDistribution = {
    low: inventoryData.filter(i => i.complexityLevel === 'low').length,
    medium: inventoryData.filter(i => i.complexityLevel === 'medium').length,
    high: inventoryData.filter(i => i.complexityLevel === 'high').length,
    critical: inventoryData.filter(i => i.complexityLevel === 'critical').length,
  }

  if (inventoryData.length === 0) {
    return (
      <div className="code-inventory-empty">
        <FileCode size={48} strokeWidth={1.5} />
        <p>No inventory data available</p>
      </div>
    )
  }

  return (
    <div className="code-inventory-report">
      {/* Header */}
      <div className="inventory-header">
        <div className="inventory-title">
          <h2><FileCode size={24} /> {title}</h2>
          <p className="inventory-subtitle">{inventoryData.length} files analyzed</p>
        </div>
        <div className="inventory-actions">
          <button
            onClick={() => exportToCSV(filteredAndSortedData)}
            className="export-btn"
            title="Export to CSV"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="inventory-summary">
        <div className="summary-card">
          <FileText size={20} />
          <div className="summary-content">
            <div className="summary-value">{totalLOC.toLocaleString()}</div>
            <div className="summary-label">Total LOC</div>
          </div>
        </div>
        <div className="summary-card">
          <FileCode size={20} color="#7aa2f7" />
          <div className="summary-content">
            <div className="summary-value">{totalParagraphs.toLocaleString()}</div>
            <div className="summary-label">Total Paragraphs</div>
          </div>
        </div>
        <div className="summary-card">
          <TrendingUp size={20} color="#e0af68" />
          <div className="summary-content">
            <div className="summary-value">{avgComplexity}</div>
            <div className="summary-label">Avg Complexity</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="complexity-distribution">
            <div className="dist-item" style={{ background: '#9ece6a' }} title={`Low: ${complexityDistribution.low}`}>
              {complexityDistribution.low}
            </div>
            <div className="dist-item" style={{ background: '#7dcfff' }} title={`Medium: ${complexityDistribution.medium}`}>
              {complexityDistribution.medium}
            </div>
            <div className="dist-item" style={{ background: '#e0af68' }} title={`High: ${complexityDistribution.high}`}>
              {complexityDistribution.high}
            </div>
            <div className="dist-item" style={{ background: '#f7768e' }} title={`Critical: ${complexityDistribution.critical}`}>
              {complexityDistribution.critical}
            </div>
          </div>
          <div className="summary-label">Complexity Distribution</div>
        </div>
      </div>

      {/* Visualization: Scatter | Treemap tabs */}
      <div style={{ margin: '0 0 20px 0' }}>
        {/* Tab header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#a9b1d6' }}>
            Program Complexity
            <span style={{ fontSize: '11px', fontWeight: 400, color: '#565f89', marginLeft: '8px' }}>
              {vizTab === 'scatter' ? 'X = LOC · Y = complexity · size = paragraphs' : 'size = lines of code · color = complexity'}
            </span>
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setVizTab('scatter')}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: `1px solid ${vizTab === 'scatter' ? '#7aa2f7' : '#2a2b3d'}`,
                background: vizTab === 'scatter' ? 'rgba(122,162,247,0.12)' : '#16161e',
                color: vizTab === 'scatter' ? '#7aa2f7' : '#565f89',
                fontSize: '11.5px',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Scatter
            </button>
            <button
              onClick={() => setVizTab('treemap')}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: `1px solid ${vizTab === 'treemap' ? '#7aa2f7' : '#2a2b3d'}`,
                background: vizTab === 'treemap' ? 'rgba(122,162,247,0.12)' : '#16161e',
                color: vizTab === 'treemap' ? '#7aa2f7' : '#565f89',
                fontSize: '11.5px',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Treemap
            </button>
          </div>
        </div>

        {/* Tab content */}
        {vizTab === 'scatter'
          ? <ScatterSection data={inventoryData} />
          : <TreemapSection data={inventoryData} />
        }
      </div>

      {/* Search and Filters */}
      <div className="inventory-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search">
              <X size={16} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`filter-btn ${showFilters ? 'active' : ''}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Complexity Level</label>
            <div className="filter-options">
              <button
                className={`filter-chip ${filterLevel === 'all' ? 'active' : ''}`}
                onClick={() => { setFilterLevel('all'); setCurrentPage(1) }}
              >
                All
              </button>
              <button
                className={`filter-chip ${filterLevel === 'low' ? 'active' : ''}`}
                onClick={() => { setFilterLevel('low'); setCurrentPage(1) }}
              >
                <CheckCircle size={14} /> Low
              </button>
              <button
                className={`filter-chip ${filterLevel === 'medium' ? 'active' : ''}`}
                onClick={() => { setFilterLevel('medium'); setCurrentPage(1) }}
              >
                <AlertTriangle size={14} /> Medium
              </button>
              <button
                className={`filter-chip ${filterLevel === 'high' ? 'active' : ''}`}
                onClick={() => { setFilterLevel('high'); setCurrentPage(1) }}
              >
                <AlertTriangle size={14} /> High
              </button>
              <button
                className={`filter-chip ${filterLevel === 'critical' ? 'active' : ''}`}
                onClick={() => { setFilterLevel('critical'); setCurrentPage(1) }}
              >
                <AlertTriangle size={14} /> Critical
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="inventory-table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('fileName')} className="sortable">
                File Name
                {sortField === 'fileName' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('loc')} className="sortable numeric">
                LOC
                {sortField === 'loc' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('paragraphs')} className="sortable numeric">
                Paragraphs
                {sortField === 'paragraphs' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('calls')} className="sortable numeric">
                Ext. Calls
                {sortField === 'calls' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('copybooks')} className="sortable numeric">
                Copybooks
                {sortField === 'copybooks' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('dataItems')} className="sortable numeric">
                Data Items
                {sortField === 'dataItems' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('complexity')} className="sortable numeric">
                Complexity
                {sortField === 'complexity' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th>Level</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={index}>
                <td className="file-name" title={item.filePath}>{item.fileName}</td>
                <td className="numeric">{item.loc.toLocaleString()}</td>
                <td className="numeric">{item.paragraphs}</td>
                <td className="numeric">{item.calls}</td>
                <td className="numeric">{item.copybooks}</td>
                <td className="numeric">{item.dataItems}</td>
                <td className="numeric">{item.complexity}</td>
                <td>
                  <span className={`complexity-badge ${item.complexityLevel}`}>
                    {item.complexityLabel}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="inventory-pagination">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages} ({filteredAndSortedData.length} files)
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

export default CodeInventoryReport
